import React, { useEffect, useState } from "react";
import ExtensionInputGroup, { StyledLabel, StyledToggleLabel } from "./ExtensionInputGroup";
import { useExtensionContext } from "../ExtensionContext";
import {
    dealMarketingTypesOptions,
    dealChannelTypesOptions,
    INHOUSE_DEAL_OBJECT,
    TOGGLE_OPTIONS,
    DEAL_WON,
    DEAL_CANCELLED,
    DEAL_LOST,
} from "../utils/constants";
import ButtonWithLabel from "app/popleads/components/ui-kit/ButtonWithLabel";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import styled from "styled-components";
import { StyledItemContainer } from "./ExtensionViewDeal";
import Toggle from "app/popleads/components/ui-kit/Toggle";
import { toast } from "react-toastify";
import useCompanyData from "app/popleads/hooks/useCompanyData";
import useActivityPreview from "app/popleads/hooks/useActivityPreview";
import moment from "moment";
import { colors } from "app/popleads/components/ui-kit/colors";
import useGetAllUnits from "app/popleads/hooks/useGetAllUnits";
import ExtensionDealAttachments from "./ExtensionDealAttachments";

const StyledContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const ExtensionNewInhouseDeal = ({ dealObject, setDealObject, validateField }) => {
    const { profile, initialDealObject } = useExtensionContext();
    const { companyUsers } = useCompanyData();
    const { departments } = useGetAllUnits();
    const [emptyInhouseInquiries, setEmptyInhouseInquiries] = useState(false);

    const formatAmount = (amount) => {
        return Number(amount) % 1 === 0 ? Math.floor(amount) : amount;
    };

    useEffect(() => {
        if (!dealObject.partner) {
            const partnerUserId = companyUsers?.find((item) => item.name === dealObject.partnerName)?.id;
            if (partnerUserId) {
                setDealObject({ ...dealObject, partner: dealObject.partnerName, partnerUserId: partnerUserId });
            }
        }
    }, [dealObject, companyUsers]);

    const validateInhouseField = (fieldName, value) => {
        switch (fieldName) {
            case "amount":
                if (!value) {
                    toast.warning("You need to enter the amount first");
                    return true;
                }
                if (isNaN(value) || value <= 0) {
                    toast.warning("Amount must be a positive number");
                    return false;
                }
                break;
            case "type":
                if (!value && dealObject.status === DEAL_WON) {
                    toast.warning("You need to enter the type first");
                    return false;
                }
                break;
            case "channelType":
                if (
                    !value &&
                    (dealObject.type === "Marketing" || dealObject.type === "Marketing Cross Sale") &&
                    dealObject.status === DEAL_WON
                ) {
                    toast.warning("You need to enter the marketing channel first");
                    return false;
                }
                break;
            default:
                return true;
        }
        return true;
    };

    const handleInputChange = (field, value) => {
        if (validateInhouseField(field, value) && validateField(field, value)) {
            setDealObject({ ...dealObject, [field]: value });
        } else if (field === "closingDate") {
            setDealObject({ ...dealObject, closingDate: value });
        }
    };

    useEffect(() => {
        if (!dealObject.selectedInquiry) return;

        const inquiry = profile.olanak.find((item) => item.activityId === dealObject.selectedInquiry);
        const partnerUserId = companyUsers?.find((item) => item.name === inquiry?.partnerName)?.id;
        if (inquiry) {
            const fieldUpdates = {
                selectedInquiry: inquiry,
                ad: inquiry.ad,
                accountName: inquiry.accountName,
                amount: formatAmount(inquiry.amount),
                closingDate: inquiry.kapanmaTarihi,
                type: inquiry.type,
                channelType: inquiry.dealTypeChannel,
                partnerName: inquiry.partnerName,
                partner: partnerUserId,
                partnerUserId: partnerUserId,
                vatIncludes: inquiry.vatIncludes === 1 ? "Yes" : "No",
                paid: inquiry.paid === 1 ? "Yes" : "No",
                courseName: inquiry.courseName,
                newCourseName: null,
            };

            getActivityPreview(inquiry.activityId).then((data) => {
                setActivityPreview(data.activity);
            });

            setDealObject((prev) => ({
                ...prev,
                ...fieldUpdates,
            }));
        }
    }, [dealObject.selectedInquiry, companyUsers]);

    const { getActivityPreview } = useActivityPreview();
    const [activityPreview, setActivityPreview] = useState(null);

    useEffect(() => {
        if (dealObject.activityId) {
            getActivityPreview(dealObject.activityId).then((data) => {
                setActivityPreview(data.activity);
            });
        }
    }, [dealObject.activityId]);

    const courseNameOptions = [
        ...(activityPreview?.topicList || []).map((item) => ({
            label: item.topicName,
            value: item.topicName,
        })),
        { label: "Type a new course name", value: "newCourseName" },
    ];

    //console.log("dealObject:", dealObject);
    //console.log("dealObject.newInquiry:", dealObject.newInquiry); // yeni inquiry oluşturulacak demek. boolean gelecek  .
    //console.log("dealObject.activityId:", dealObject.activityId); // deal oluşturulmuş demek. int gelecek.
    //console.log("dealObject.selectedInquiry:", dealObject.selectedInquiry); // inquiry seçilmiş demek. object gelecek.

    // mutually exclusive durumlar:
    // 1. newInquiry true ise selectedInquiry olamaz.

    // senaryo 1: activityId yok: deal oluşturulmamış.
    // senaryo 1.1: newInquiry false: mevcut deal seçilecek. --DONE
    // senaryo 1.2: newInquiry true: yeni deal oluşturulacak. --DONE
    // senaryo 2: activityId var: deal oluşturulmuş.

    const inhouseInquiries = profile?.inhouseInquiries?.filter(
        (item, index, self) => index === self.findIndex((obj) => obj.value === item.value)
    );

    useEffect(() => {
        if (profile?.inhouseInquiries.length === 0) {
            setDealObject({ ...dealObject, selectedInquiry: null, newInquiry: true });
            setEmptyInhouseInquiries(true);
        }
    }, [profile?.inhouseInquiries]);

    return (
        <StyledContainer>
            {/* 1. senaryo activityID yok. started */}
            {!dealObject.activityId && (
                <>
                    {/* activityId yok, yeni deal oluşturulacak. */}
                    {/* newInquiry */}
                    {!emptyInhouseInquiries && (
                        <ExtensionInputGroup
                            variant="l3rnTech"
                            label="Select Existing Inquiry"
                            type="select"
                            options={inhouseInquiries ?? []}
                            disabled={dealObject.newInquiry}
                            value={
                                dealObject.selectedInquiry
                                    ? {
                                          label: dealObject.selectedInquiry.activityName,
                                          value: dealObject.selectedInquiry.activityId,
                                      }
                                    : ""
                            }
                            onChange={(selected) => {
                                handleInputChange("selectedInquiry", selected.value);
                                setDealObject({ ...dealObject, selectedInquiry: selected.value, newInquiry: false });
                            }}
                            placeholder="Select Existing Inquiry"
                        />
                    )}

                    {/* 1.1: newInquiry false: mevcut deal seçilecek. */}
                    {!dealObject.newInquiry && (
                        <>
                            {/* newInquiry false: mevcut deal seçilecek. */}
                            {dealObject.selectedInquiry && (
                                <>
                                    {/* dealObject.selectedInquiry var */}

                                    <ExtensionInputGroup
                                        variant="l3rnTech"
                                        label="Deal Name"
                                        type="input"
                                        inpType="text"
                                        onChange={(e) => {
                                            const value = e.target ? e.target.value : e;
                                            handleInputChange("ad", value);
                                        }}
                                        value={dealObject.ad}
                                        placeholder="Enter Deal Name"
                                    />

                                    <ExtensionInputGroup
                                        variant="l3rnTech"
                                        label="Course Name"
                                        type="select"
                                        inpType="text"
                                        onChange={(selected) => {
                                            handleInputChange("courseName", selected.value);
                                        }}
                                        options={courseNameOptions}
                                        value={
                                            dealObject.courseName
                                                ? dealObject.courseName === "newCourseName"
                                                    ? { label: "Type a new course name", value: "newCourseName" }
                                                    : { label: dealObject.courseName, value: dealObject.courseName }
                                                : ""
                                        }
                                        placeholder="Select Course Name"
                                    />

                                    {dealObject?.courseName === "newCourseName" && (
                                        <ExtensionInputGroup
                                            variant="l3rnTech"
                                            label="New Course Name"
                                            type="input"
                                            inpType="text"
                                            onChange={(e) => {
                                                const value = e.target ? e.target.value : e;
                                                handleInputChange("newCourseName", value);
                                            }}
                                            value={dealObject.newCourseName}
                                            placeholder="Enter New Course Name"
                                        />
                                    )}

                                    <ExtensionInputGroup
                                        variant="l3rnTech"
                                        label="Account Name"
                                        type="input"
                                        inpType="text"
                                        onChange={(e) => {
                                            const value = e.target ? e.target.value : e;
                                            handleInputChange("accountName", value);
                                        }}
                                        value={dealObject.accountName}
                                        placeholder="Enter Account Name"
                                    />

                                    <ExtensionInputGroup
                                        variant="l3rnTech"
                                        label="Amount USD"
                                        type="input"
                                        inpType="number"
                                        min={0}
                                        minLength={dealObject.amount ? 0 : 1}
                                        leftIcon="dollar-sign"
                                        onChange={(e) => {
                                            const value = e.target ? e.target.value : e;
                                            handleInputChange("amount", value);
                                        }}
                                        value={dealObject.amount}
                                        placeholder="Enter Unit Amount"
                                    />

                                    <ExtensionInputGroup
                                        variant="l3rnTech"
                                        label="Set Closing Date"
                                        type="date"
                                        onChange={(date) => {
                                            handleInputChange("closingDate", date);
                                        }}
                                        value={dealObject.closingDate}
                                        dateFormat="dd/MM/yyyy"
                                        placeholder="Select Closing Date"
                                    />

                                    <StyledItemContainer marginBottom="0">
                                        <StyledToggleLabel color={colors.neutral[900]}>Paid</StyledToggleLabel>
                                        <Toggle
                                            variant="l3rnTech"
                                            setSelected={(value) => {
                                                handleInputChange("paid", value);
                                            }}
                                            selected={dealObject.paid}
                                            options={TOGGLE_OPTIONS}
                                        />
                                    </StyledItemContainer>

                                    <ExtensionInputGroup
                                        variant="l3rnTech"
                                        label="Select Type"
                                        type="select"
                                        options={dealMarketingTypesOptions}
                                        value={
                                            dealObject.type ? { label: dealObject.type, value: dealObject.type } : ""
                                        }
                                        onChange={(selected) => {
                                            handleInputChange("type", selected.value);
                                        }}
                                        placeholder="Select Type"
                                    />

                                    {(dealObject.type === "Marketing" ||
                                        dealObject.type === "Marketing Cross Sale") && (
                                        <ExtensionInputGroup
                                            variant="l3rnTech"
                                            label="Select Marketing Channel"
                                            type="select"
                                            options={dealChannelTypesOptions}
                                            value={
                                                dealObject.channelType
                                                    ? { label: dealObject.channelType, value: dealObject.channelType }
                                                    : ""
                                            }
                                            onChange={(selected) => {
                                                handleInputChange("channelType", selected.value);
                                            }}
                                            placeholder="Select Marketing Channel"
                                        />
                                    )}

                                    <ExtensionInputGroup
                                        variant="l3rnTech"
                                        label="Partner"
                                        type="select"
                                        options={companyUsers.map((user) => ({
                                            label: user.name,
                                            value: user.id,
                                        }))}
                                        onChange={(selected) => {
                                            setDealObject({
                                                ...dealObject,
                                                partnerName: selected?.label || "",
                                                partner: selected?.value || "",
                                                partnerUserId: selected?.value || "",
                                            });
                                        }}
                                        value={
                                            dealObject?.partnerName
                                                ? {
                                                      label: dealObject?.partnerName,
                                                      value: dealObject?.partner,
                                                  }
                                                : ""
                                        }
                                        placeholder="Select Partner"
                                        isClearable={true}
                                    />

                                    <div>
                                        <ButtonWithText
                                            variant="l3rnTech"
                                            size="small"
                                            buttonStyle="borderless"
                                            onClick={() =>
                                                setDealObject({
                                                    ...initialDealObject,
                                                    newInquiry: true,
                                                    selectedInquiry: null,
                                                    ...INHOUSE_DEAL_OBJECT,
                                                    closingDate: moment().valueOf(),
                                                    dealCampaignType: dealObject?.dealCampaignType,
                                                    dealPotential: dealObject?.dealPotential,
                                                    status: dealObject?.status,
                                                })
                                            }
                                            style={{ marginTop: "8px", padding: "0", height: "24px" }}
                                        >
                                            Or Create a New Inquiry
                                        </ButtonWithText>
                                    </div>
                                </>
                            )}
                            {!dealObject.selectedInquiry && (
                                <>
                                    {/* dealObject.selectedInquiry yok */}
                                    <ButtonWithLabel
                                        variant="l3rnTech"
                                        buttonStyle="default"
                                        size="circle"
                                        icon={"add"}
                                        iconType={"material"}
                                        label={"Create New Inquiry"}
                                        onClick={() =>
                                            setDealObject({
                                                ...dealObject,
                                                selectedInquiry: null,
                                                newInquiry: true,
                                                ...INHOUSE_DEAL_OBJECT,
                                            })
                                        }
                                    />
                                </>
                            )}
                        </>
                    )}
                    {/* 1.2: newInquiry true: yeni deal oluşturulacak. */}
                    {dealObject.newInquiry && (
                        <>
                            {/* newInquiry true: yeni deal oluşturulacak. */}
                            {!emptyInhouseInquiries && (
                                <div>
                                    <ButtonWithText
                                        variant="l3rnTech"
                                        size="small"
                                        buttonStyle="borderless"
                                        onClick={() =>
                                            setDealObject({ ...dealObject, newInquiry: false, ...INHOUSE_DEAL_OBJECT })
                                        }
                                        style={{ padding: "0", height: "24px" }}
                                    >
                                        Or Select Existing Inquiry
                                    </ButtonWithText>
                                </div>
                            )}

                            <ExtensionInputGroup
                                variant="l3rnTech"
                                label="Amount USD"
                                type="input"
                                inpType="number"
                                min={0}
                                minLength={1}
                                leftIcon="dollar-sign"
                                onChange={(e) => {
                                    const value = e.target ? e.target.value : e;
                                    handleInputChange("amount", value);
                                }}
                                value={dealObject.amount}
                                placeholder="Enter Amount"
                            />

                            <ExtensionInputGroup
                                variant="l3rnTech"
                                label="Partner"
                                type="select"
                                options={companyUsers.map((user) => ({
                                    label: user.name,
                                    value: user.id,
                                }))}
                                onChange={(selected) => {
                                    setDealObject({
                                        ...dealObject,
                                        partnerName: selected?.label || "",
                                        partner: selected?.value || "",
                                        partnerUserId: selected?.value || "",
                                    });
                                }}
                                value={
                                    dealObject?.partnerName
                                        ? {
                                              label: dealObject?.partnerName,
                                              value: dealObject?.partner,
                                          }
                                        : ""
                                }
                                placeholder="Select Partner"
                                isClearable={true}
                            />
                            {(dealObject.status === DEAL_WON ||
                                dealObject.status === DEAL_CANCELLED ||
                                dealObject.status === DEAL_LOST) && (
                                <>
                                    <ExtensionInputGroup
                                        variant="l3rnTech"
                                        label="Set Closing Date"
                                        type="date"
                                        onChange={(date) => {
                                            handleInputChange("closingDate", date);
                                        }}
                                        value={dealObject.closingDate}
                                        dateFormat="dd/MM/yyyy"
                                        placeholder="Select Closing Date"
                                    />

                                    <ExtensionInputGroup
                                        variant="l3rnTech"
                                        label="Select Type"
                                        type="select"
                                        options={dealMarketingTypesOptions}
                                        value={
                                            dealObject.type ? { label: dealObject.type, value: dealObject.type } : ""
                                        }
                                        onChange={(selected) => {
                                            handleInputChange("type", selected.value);
                                        }}
                                        placeholder="Select Type"
                                    />

                                    {(dealObject.type === "Marketing" ||
                                        dealObject.type === "Marketing Cross Sale") && (
                                        <ExtensionInputGroup
                                            variant="l3rnTech"
                                            label="Select Marketing Channel"
                                            type="select"
                                            options={dealChannelTypesOptions}
                                            value={
                                                dealObject.channelType
                                                    ? { label: dealObject.channelType, value: dealObject.channelType }
                                                    : ""
                                            }
                                            onChange={(selected) => {
                                                handleInputChange("channelType", selected.value);
                                            }}
                                            placeholder="Select Marketing Channel"
                                        />
                                    )}

                                    {dealObject.status.toLowerCase() !== "lost" &&
                                        dealObject.status.toLowerCase() !== "cancelled" && (
                                            <>
                                                <ExtensionInputGroup
                                                    variant="l3rnTech"
                                                    label="Paid Amount"
                                                    type="input"
                                                    inpType="number"
                                                    min={0}
                                                    leftIcon="dollar-sign"
                                                    onKeyDown={(e) => {
                                                        if (e.key === "-" || e.key === "e" || e.key === "+") {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(e) => {
                                                        const value = e.target ? e.target.value : e;
                                                        handleInputChange("paidAmount", value);
                                                    }}
                                                    value={dealObject.paidAmount}
                                                    placeholder="Enter Paid Amount"
                                                    disabled={
                                                        dealObject?.createAnotherDeal || dealObject?.toBe === DEAL_WON
                                                    }
                                                />

                                                <ExtensionInputGroup
                                                    variant="l3rnTech"
                                                    label="Course Name"
                                                    type="input"
                                                    inpType="text"
                                                    onChange={(e) => {
                                                        const value = e.target ? e.target.value : e;
                                                        handleInputChange("courseName", value);
                                                    }}
                                                    value={dealObject.courseName}
                                                    placeholder="Enter Course Name"
                                                />

                                                <StyledItemContainer>
                                                    <StyledToggleLabel>VAT Included</StyledToggleLabel>
                                                    <Toggle
                                                        variant="l3rnTech"
                                                        setSelected={(value) => {
                                                            handleInputChange("vatIncludes", value);
                                                        }}
                                                        selected={dealObject.vatIncludes}
                                                        options={TOGGLE_OPTIONS}
                                                    />
                                                </StyledItemContainer>
                                                <div>
                                                    <StyledLabel>Upload Document</StyledLabel>
                                                    <ExtensionDealAttachments maxFiles={5} />
                                                </div>
                                            </>
                                        )}
                                </>
                            )}
                        </>
                    )}
                </>
            )}
            {/* 1. senaryo activityID yok. ended */}
            {/* 2. senaryo activityID var. started */}
            {dealObject.activityId && (
                <>
                    {/* activityId var, deal oluşturulmuş. */}
                    <ExtensionInputGroup
                        label="Deal Name"
                        type="input"
                        inpType="text"
                        onChange={(e) => {
                            const value = e.target ? e.target.value : e;
                            handleInputChange("ad", value);
                        }}
                        value={dealObject.ad}
                        placeholder="Enter Deal Name"
                    />
                    <ExtensionInputGroup
                        label="Course Name"
                        type="select"
                        inpType="text"
                        onChange={(selected) => {
                            handleInputChange("courseName", selected.value);
                        }}
                        options={courseNameOptions}
                        value={
                            dealObject.courseName
                                ? dealObject.courseName === "newCourseName"
                                    ? { label: "Type a new course name", value: "newCourseName" }
                                    : { label: dealObject.courseName, value: dealObject.courseName }
                                : ""
                        }
                        placeholder="Please Select Course Name"
                    />

                    {dealObject?.courseName === "newCourseName" && (
                        <ExtensionInputGroup
                            label="New Course Name"
                            type="input"
                            inpType="text"
                            onChange={(e) => {
                                const value = e.target ? e.target.value : e;
                                handleInputChange("newCourseName", value);
                            }}
                            value={dealObject.newCourseName}
                            placeholder="Enter New Course Name"
                        />
                    )}
                    <ExtensionInputGroup
                        label="Account Name"
                        type="input"
                        inpType="text"
                        onChange={(e) => {
                            const value = e.target ? e.target.value : e;
                            handleInputChange("accountName", value);
                        }}
                        value={dealObject.accountName}
                        placeholder="Enter Account Name"
                    />
                    <ExtensionInputGroup
                        label="Amount USD"
                        type="input"
                        inpType="number"
                        min={0}
                        minLength={1}
                        leftIcon="dollar-sign"
                        onChange={(e) => {
                            const value = e.target ? e.target.value : e;
                            handleInputChange("amount", value);
                        }}
                        value={dealObject.amount}
                        placeholder="Enter Amount"
                    />
                    <ExtensionInputGroup
                        label="Paid Amount"
                        type="input"
                        inpType="number"
                        min={0}
                        leftIcon="dollar-sign"
                        onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "e" || e.key === "+") {
                                e.preventDefault();
                            }
                        }}
                        onChange={(e) => {
                            const value = e.target ? e.target.value : e;
                            handleInputChange("paidAmount", value);
                        }}
                        value={dealObject.paidAmount}
                        disabled={true}
                        placeholder="Enter Paid Amount"
                    />
                    <ExtensionInputGroup
                        label="Set Closing Date"
                        type="date"
                        onChange={(date) => {
                            handleInputChange("closingDate", date);
                        }}
                        value={dealObject.closingDate}
                        dateFormat="dd/MM/yyyy"
                        placeholder="Select Closing Date"
                    />
                    <ExtensionInputGroup
                        label="Select Type"
                        type="select"
                        options={dealMarketingTypesOptions}
                        value={dealObject.type ? { label: dealObject.type, value: dealObject.type } : ""}
                        onChange={(selected) => {
                            handleInputChange("type", selected.value);
                        }}
                        placeholder="Select Type"
                    />
                    {(dealObject.type === "Marketing" || dealObject.type === "Marketing Cross Sale") && (
                        <ExtensionInputGroup
                            label="Select Marketing Channel"
                            type="select"
                            options={dealChannelTypesOptions}
                            value={
                                dealObject.channelType
                                    ? { label: dealObject.channelType, value: dealObject.channelType }
                                    : ""
                            }
                            onChange={(selected) => {
                                handleInputChange("channelType", selected.value);
                            }}
                            placeholder="Select Marketing Channel"
                        />
                    )}
                    <ExtensionInputGroup
                        label="Department"
                        type="select"
                        options={departments}
                        onChange={(selected) => {
                            if (selected) {
                                const newDealObject = { ...dealObject };
                                newDealObject.unitId = selected.value;
                                newDealObject.unitName = selected.label;
                                setDealObject(newDealObject);
                            }
                        }}
                        value={
                            dealObject?.unitName
                                ? {
                                      label: dealObject?.unitName,
                                      value: dealObject?.unitId,
                                  }
                                : ""
                        }
                        placeholder="Please select department"
                        minMenuHeight={300}
                    />
                    <ExtensionInputGroup
                        label="Partner"
                        type="select"
                        options={companyUsers.map((user) => ({
                            label: user.name,
                            value: user.id,
                        }))}
                        onChange={(selected) => {
                            setDealObject({
                                ...dealObject,
                                partnerName: selected?.label || "",
                                partner: selected?.value || "",
                                partnerUserId: selected?.value || "",
                            });
                        }}
                        value={
                            dealObject?.partnerName
                                ? {
                                      label: dealObject?.partnerName,
                                      value: dealObject?.partner,
                                  }
                                : ""
                        }
                        isClearable={true}
                        placeholder="Select Partner"
                    />
                    <StyledItemContainer marginBottom="0">
                        <StyledToggleLabel>VAT Included</StyledToggleLabel>
                        <Toggle
                            setSelected={(value) => {
                                handleInputChange("vatIncludes", value);
                            }}
                            selected={dealObject.vatIncludes}
                            options={TOGGLE_OPTIONS}
                        />
                    </StyledItemContainer>
                </>
            )}
            {/* 2. senaryo activityID var. ended */}
        </StyledContainer>
    );
};

export default ExtensionNewInhouseDeal;
